import { datadogLogs } from '@datadog/browser-logs';
import { GoogleTagManager, tracking } from '@iwoca/frontend-tracking-library';
import _ from 'lodash';
import { stringify } from 'qs';
import {
    fork,
    put,
    take,
    select,
    cancel,
    takeLatest
} from 'redux-saga/effects';

import {
    convertActiveStepIdToStepPathName,
    BUSINESS_ACCOUNTS_STEP_ID,
    MY_ACCOUNTS_STEP_ID,
    PERSONAL_INFORMATION_STEP_ID
} from '@src/containers/Routes/helper/stepActiveIds.helper';
import { getQueryParams } from '@src/helpers/helpers';
import { getRequestedAmount } from '@src/utils/selectors';

import * as LoanDecisionActions from './LoanDecision.actions';
import * as selectors from './LoanDecision.selectors';
import { nextStep } from '../../actions';
import { redirectToURL } from '../../helpers/redirection';
import { GET_DOCUMENT_FEEDBACK } from '../../lendingAPI/lendingAPI.actions';
import { putAPI } from '../../lendingAPI/lendingAPIHelpers';
import {
    getRequestedProductType,
    getSuggestedProductType,
    hasAutomatedDecision as hasAutomatedDecisionSelector,
    isImpersonating as isImpersonatingSelector,
    getCountryCode
} from '../../lendingAPI/lendingAPISelector';
import * as IwDataDogLogger from '../../loggers/IwDataDogLogger';
import { getAmplitudeDefaultEventProperties } from '../../tracking/amplitudeEvents';
import Tracking from '../../tracking/IwTracking';
import * as AutomatedLendingActions from '../AutomatedLendingDecision/AutomatedLendingDecision.actions';
import {
    KNOWN_ERROR_CODES,
    KNOWN_ERROR_CODE_PRIORITY
} from '../AutomatedLendingDecision/AutomatedLendingDecision.reducer';
import { saveCustomerState } from '../AutomatedLendingDecision/AutomatedLendingDecisionUtils';
import * as ExitPollActions from '../AutomatedLendingDecision/ExitPoll/ExitPoll.actions';
import * as exitPollSagas from '../AutomatedLendingDecision/ExitPoll/ExitPoll.sagas';
import { OPEN_MODAL, CLOSE_MODAL } from '../Modals/Modals.actions';
import { handleNewOffers } from '../Offers/offers/Offers.sagas';
import { isNewOffersEnabled } from '../Offers/offers/offersHelpers';
import { navigateTo } from '../Routes/Navigation.action';
import { getBasePath } from '../Routes/Navigation.selector';

function* loanDecisionSaga() {
    yield takeLatest(LoanDecisionActions.GET_LOAN_DECISION, function* (action) {
        const newOffersEnabled = yield select((state) =>
            isNewOffersEnabled(state)
        );

        if (newOffersEnabled && action.payload !== 'newOffers') {
            yield* handleNewOffers();
            return;
        }

        yield put({ type: OPEN_MODAL, modalId: 'automatedLendingDecision' });

        yield fork(runLoanDecision);
        yield take(CLOSE_MODAL);
        yield put({ type: LoanDecisionActions.CANCEL_GET_LOAN_DECISION });
        yield put({ type: ExitPollActions.HIDE_EXIT_POLL });
    });
}

function* runLoanDecision() {
    const { currentStep } = yield select((state) => ({
        currentStep: selectors.getCurrentStep(state)
    }));

    try {
        yield put({ type: AutomatedLendingActions.SHOW_CHECKING_APPLICATION });
        yield* saveCustomerState();

        const exitPollEnabled = yield* exitPollSagas.isExitPollEnabled();

        if (exitPollEnabled) {
            yield put({ type: ExitPollActions.SHOW_EXIT_POLL });
        }

        const application_id = yield select(
            ({ lendingAPI }) => lendingAPI.application.data.application_id
        );

        const { payload: offersResponsePayload } = yield putAPI({
            type: 'api/GET_OFFERS',
            body: { application_id }
        });

        const offerCreationStatus =
            offersResponsePayload?.offer_creation_status;
        const selectedOffer = offersResponsePayload?.offers[0];

        if (
            offerCreationStatus === 'declined' ||
            offerCreationStatus === 'deferred' ||
            selectedOffer.offer_type === 'confirmed'
        ) {
            if (offerCreationStatus === 'declined') {
                try {
                    tracking.ampli.offerDeclined({
                        decline_reason:
                            offersResponsePayload.decline_reason[0].reason
                    });
                } catch (error) {
                    IwDataDogLogger.logError({
                        message: `Failed to set amplitude tracking offer declined`,
                        error
                    });
                }
            }

            yield* completeSignup();

            Tracking.completeSignupStep(PERSONAL_INFORMATION_STEP_ID);
            yield put(
                Tracking.trackLeadQualityAction(PERSONAL_INFORMATION_STEP_ID)
            );

            redirectToURL(ACCOUNT_PAGES_URL);
            yield cancel();
        }

        if (!offersResponsePayload?.offers?.length) {
            throw new Error('unexpected product offer state');
        }

        if (currentStep !== BUSINESS_ACCOUNTS_STEP_ID) {
            yield* checkCustomerDocRequirements(selectedOffer);
            // TODO: Error handling for offers
            checkProductRequirementErrors(selectedOffer);

            Tracking.completeSignupStep(PERSONAL_INFORMATION_STEP_ID);
            yield put(
                Tracking.trackLeadQualityAction(PERSONAL_INFORMATION_STEP_ID)
            );
        }

        yield* handleSuggestedProduct(selectedOffer);
    } catch (errors) {
        yield put({
            type: LoanDecisionActions.LOAN_DECISION_ERROR,
            errorCode: errors && errors.length ? errors[0] : null
        });

        datadogLogs.logger.error(
            'Loan Decision Error',
            {
                location: 'runLoanDecision'
            },
            errors
        );
    }
}

export function checkProductRequirementErrors(suggestedProduct) {
    const approvalRequirementErrors = selectors
        .getApprovalRequirementErrors(suggestedProduct)
        .filter((errorCode) => _.includes(KNOWN_ERROR_CODES, errorCode))
        .sort(
            (a, b) =>
                KNOWN_ERROR_CODE_PRIORITY[a] - KNOWN_ERROR_CODE_PRIORITY[b]
        );
    if (approvalRequirementErrors.length) {
        throw approvalRequirementErrors;
    }
}

function* handleSuggestedProduct(selectedOffer) {
    const { currentStep, isImpersonating, countryCode } = yield select(
        (state) => ({
            currentStep: selectors.getCurrentStep(state),
            countryCode: getCountryCode(state),
            isImpersonating: isImpersonatingSelector(state)
        })
    );

    const hasCompletedDocRequirements =
        !selectors.hasRequiredDocuments(selectedOffer);

    if (currentStep === BUSINESS_ACCOUNTS_STEP_ID) {
        // NOTE: This doesn't do anything, the call for the api is just to trigger a process of collecting document feedback, which might be used at a later date.
        if (countryCode !== 'de') {
            yield put({
                type: `api/${GET_DOCUMENT_FEEDBACK}`
            });
        }

        Tracking.completeSignupStep(BUSINESS_ACCOUNTS_STEP_ID, {
            isImpersonating
        });
    }

    if (hasCompletedDocRequirements) {
        yield* handleDecision();
    } else {
        throw new Error('unexpected product offer state');
    }
}

export function* handleDecision() {
    yield* completeSignup();

    yield* redirectAfterSignup();
}

function* completeSignup() {
    const { companyType } = yield select((state) => ({
        companyType: selectors.getCompanyTypeFromForm(state)
    }));

    GoogleTagManager.track('TRACK_FINANCEADS_COMPANY_TYPE', {
        companyType
    });

    yield* exitPollSagas.waitForLoadingExitPoll();
    const exitPollHasAnswer = yield* exitPollSagas.hasExitPollBeenAnswered();

    if (!exitPollHasAnswer) {
        yield put({
            type: ExitPollActions.EXIT_POLL_ANSWER,
            value: { answer: 'no_answer' },
            step: MY_ACCOUNTS_STEP_ID
        });
    }

    yield* saveCustomerState(MY_ACCOUNTS_STEP_ID);

    yield amplitudeTrackSignupCompleted();
}

export function* amplitudeTrackSignupCompleted() {
    try {
        const suggestedProductType = yield select((state) =>
            getSuggestedProductType(state)
        );
        const requestedProductType = yield select((state) =>
            getRequestedProductType(state)
        );

        const requestedAmount = yield select((state) =>
            getRequestedAmount(state)
        );

        const hasAutomatedDecision = yield select((state) =>
            hasAutomatedDecisionSelector(state)
        );

        tracking.ampli.signupCompleted({
            decisionType: hasAutomatedDecision ? 'auto' : 'manual',
            suggestedProduct: suggestedProductType,
            requestedProduct: requestedProductType,
            requestedAmount,
            ...getAmplitudeDefaultEventProperties()
        });
    } catch (error) {
        IwDataDogLogger.logError({
            message: `Failed to Set Amplitude User Property Signup completed`,
            error
        });
    }
}

export function* checkCustomerDocRequirements(selectedOffer) {
    const hasApprovalRequirements =
        selectors.hasRequiredDocuments(selectedOffer);
    const isUKLocale = yield select((state) => getCountryCode(state) === 'uk');

    if (!hasApprovalRequirements) return;

    yield putAPI({
        type: 'api/GET_APPLICATION'
    });

    const urlQueryParams = stringify(getQueryParams());
    const basePath = yield select((state) => getBasePath(state));

    const pathname = convertActiveStepIdToStepPathName(
        BUSINESS_ACCOUNTS_STEP_ID,
        isUKLocale
    );

    yield put(nextStep(BUSINESS_ACCOUNTS_STEP_ID));

    yield* saveCustomerState(BUSINESS_ACCOUNTS_STEP_ID);

    yield put(
        navigateTo(
            `${basePath}/${pathname}${
                urlQueryParams ? `?${urlQueryParams}` : ''
            }`
        )
    );

    yield put({ type: CLOSE_MODAL });
    yield cancel();
}

export const ACCOUNT_PAGES_URL = '/my_account/loan/';
export const ACCOUNT_PAGES_APPROVED_URL = `${ACCOUNT_PAGES_URL}#approved`;
export const ACCOUNT_PAGES_DECLINED_URL = `${ACCOUNT_PAGES_URL}#declined`;

function* getRedirectBasePath() {
    return ACCOUNT_PAGES_URL;
}

function* redirectAfterSignup() {
    const redirectURL = yield getRedirectBasePath();
    return redirectToURL(redirectURL);
}

export default [fork(loanDecisionSaga)];
